import { classnames } from '@/helpers/general';
import { _t } from '@/locale';
import { KeyboardEvent, MouseEvent, ReactNode, useRef } from 'react';
import TabCountBadge, { TabCountSkeleton } from './components/TabCountBadge';
import { getNextTabIndex, getSerpTabElementId } from './SerpTabs.helpers';
import { SerpTabId, SerpTabItem } from './SerpTabs.types';

type SerpTabsProps = {
  tabs: SerpTabItem[];
  activeTab: SerpTabId;
  onTabChange: (id: SerpTabId) => void;
  panelId: string;
  trailing?: ReactNode;
  className?: string;
};

const TAB_LABEL_KEYS = {
  places: 'features.unified-search.SerpTabs.places',
  sistaminuten: 'features.unified-search.SerpTabs.sistaminuten',
} as const;

const SerpTabs = ({ tabs, activeTab, onTabChange, panelId, trailing, className }: SerpTabsProps) => {
  const tabRefs = useRef<Array<HTMLAnchorElement | null>>([]);

  const handleClick = (event: MouseEvent<HTMLAnchorElement>, id: SerpTabId) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    onTabChange(id);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLAnchorElement>, index: number) => {
    if (event.altKey || event.metaKey || event.ctrlKey || event.shiftKey) return;
    if (event.key === ' ') {
      event.preventDefault();
      onTabChange(tabs[index].id);
      return;
    }
    const nextIndex = getNextTabIndex(event.key, index, tabs.length);
    if (nextIndex === null) return;
    event.preventDefault();
    tabRefs.current[nextIndex]?.focus();
  };

  const hasActiveTab = tabs.some((tab) => tab.id === activeTab);

  return (
    <div className={classnames('border-border_bold flex items-center border-b', className)}>
      <div
        role="tablist"
        aria-label={_t('features.unified-search.SerpTabs.ariaLabel')}
        aria-busy={tabs.some((tab) => tab.countLoading) || undefined}
        className="gap-md no-scrollbar flex min-w-0 overflow-x-auto">
        {tabs.map((tab, index) => {
          const isActive = tab.id === activeTab;
          return (
            <a
              key={tab.id}
              ref={(element) => {
                tabRefs.current[index] = element;
              }}
              id={getSerpTabElementId(panelId, tab.id)}
              role="tab"
              href={tab.href}
              aria-selected={isActive}
              aria-controls={panelId}
              tabIndex={isActive || (!hasActiveTab && index === 0) ? 0 : -1}
              onClick={(event) => handleClick(event, tab.id)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              className={classnames(
                'gap-xs px-2xs py-sm text-l focus-visible:outline-border_focus flex shrink-0 items-center whitespace-nowrap border-b focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2',
                isActive
                  ? 'text-fg_primary border-border_selected font-bold'
                  : 'text-fg_secondary border-transparent font-normal',
              )}>
              {_t(TAB_LABEL_KEYS[tab.id])}
              {tab.countLoading ? (
                <TabCountSkeleton />
              ) : (
                tab.count !== undefined && <TabCountBadge count={tab.count} isActive={isActive} />
              )}
            </a>
          );
        })}
      </div>
      {trailing && <div className="gap-xs ml-auto flex shrink-0 items-center">{trailing}</div>}
    </div>
  );
};

export default SerpTabs;
